import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import axios from '../utils/axiosConfig';

// Cache for loaded characters
const characterCache = new Map();

// Cache for loaders
const loaders = {
  gltf: null,
  glb: null,
  fbx: null,
  obj: null
};

// Initialize loaders
const getLoader = (format) => {
  const type = format.toLowerCase();
  
  if (loaders[type]) {
    return loaders[type];
  }

  switch (type) {
    case 'gltf':
    case 'glb':
      if (!loaders.gltf) {
        const loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('/draco/');
        loader.setDRACOLoader(dracoLoader);
        loaders.gltf = loader;
        loaders.glb = loader;
      }
      return loaders.gltf;
    
    case 'fbx':
      if (!loaders.fbx) {
        loaders.fbx = new FBXLoader();
      }
      return loaders.fbx;
    
    case 'obj':
      if (!loaders.obj) {
        loaders.obj = new OBJLoader();
      }
      return loaders.obj;
    
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
};

// Load character from URL
export const loadCharacter = async (url, format) => {
  const cacheKey = `${url}:${format}`;
  
  if (characterCache.has(cacheKey)) {
    return characterCache.get(cacheKey);
  }

  const loader = getLoader(format);
  const type = format.toLowerCase();
  
  try {
    const character = await new Promise((resolve, reject) => {
      loader.load(
        url,
        (data) => {
          if (type === 'fbx') {
            // FBX loader returns the model directly with animations
            resolve(data);
          } else {
            // GLTF/GLB returns { scene, animations }
            const model = data.scene || data;
            model.animations = data.animations || [];
            resolve(model);
          }
        },
        undefined,
        reject
      );
    });

    characterCache.set(cacheKey, character);
    return character;
  } catch (error) {
    console.error('Error loading character:', error);
    throw new Error(`Failed to load character: ${error.message}`);
  }
};

// API functions
const api = axios.create({
  baseURL: '/api/characters',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Upload character file
export const uploadCharacter = async (formData) => {
  try {
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading character:', error);
    throw error.response?.data || error;
  }
};

// Fetch all characters
export const fetchCharacters = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.project) params.append('project', filters.project);
    if (filters.scene) params.append('scene', filters.scene);
    
    const response = await api.get('/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching characters:', error);
    throw error.response?.data || error;
  }
};

// Get a specific character
export const getCharacter = async (id) => {
  try {
    const response = await api.get(`/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching character:', error);
    throw error.response?.data || error;
  }
};

// Update character
export const updateCharacter = async (id, updates) => {
  try {
    const response = await api.patch(`/${id}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating character:', error);
    throw error.response?.data || error;
  }
};

// Delete character
export const deleteCharacter = async (id) => {
  try {
    const response = await api.delete(`/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting character:', error);
    throw error.response?.data || error;
  }
};

// Clear character cache
export const clearCharacterCache = () => {
  characterCache.clear();
};
